import { InfoCircleOutlined } from '@ant-design/icons';
import { Flex, Form, Select, Tooltip, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  LAST_INTEGRATION_PROP,
  LOYALTY_PROGRAM_SENDOUTS,
  RULES_IS_REQUIRE,
} from '@app/constants';
import type { NSendoutsApi } from '@app/services/rtkQuery/Sendouts/types';

import type { BaseOptionType } from 'antd/es/select';
import type { FC } from 'react';

const { Text } = Typography;
const { useFormInstance } = Form;

interface Props {
  prepareSendoutCreation: NSendoutsApi.PrepareSendoutCreationResponse | null;
  channel: string;
}

type Option = {
  value: number | string;
  label: string;
};

export const IntegrationSelect: FC<Props> = ({
  prepareSendoutCreation,
  channel,
}) => {
  const form = useFormInstance();

  const allIntegrations: BaseOptionType[] = useMemo(() => {
    const options: Option[] =
      prepareSendoutCreation?.Integrations.map(
        ({ Id, IntegrationName, SocialType }) => ({
          value: Id,
          label: IntegrationName,
          name: SocialType,
        })
      ) || [];
    options.unshift(LAST_INTEGRATION_PROP);

    return options;
  }, [prepareSendoutCreation?.Integrations]);

  const [filteredIntegrations, setFilteredIntegrations] =
    useState<BaseOptionType[]>(allIntegrations);

  useEffect(() => {
    const filteredIntegrations = allIntegrations.filter(
      ({ name }) => name === channel
    );

    if (channel === LOYALTY_PROGRAM_SENDOUTS) {
      form.setFieldValue('integration', filteredIntegrations[0]?.value);
    }

    filteredIntegrations.unshift(LAST_INTEGRATION_PROP);

    if (channel) {
      setFilteredIntegrations([...filteredIntegrations]);
    }
  }, [allIntegrations, channel, form]);

  const onChange = useCallback(
    (value: number | string) => form.setFieldsValue({ integration: value }),
    [form]
  );

  const isDisabled = useMemo(() => {
    return !channel || channel === LOYALTY_PROGRAM_SENDOUTS;
  }, [channel]);

  return (
    <Flex vertical gap={8}>
      <Form.Item
        label={
          <Text>
            Интеграция{' '}
            <Tooltip title="Чтобы разблокировать поле, выберите канал, отличный от последнего активного.">
              <InfoCircleOutlined />
            </Tooltip>
          </Text>
        }
        colon
        name="integration"
        wrapperCol={{ offset: 2, span: 32 }}
        rules={isDisabled ? [] : [...RULES_IS_REQUIRE]}
        required
      >
        <Select
          placeholder="Выберите интеграцию"
          showSearch
          optionFilterProp="label"
          options={filteredIntegrations}
          style={{ width: '100%' }}
          onChange={onChange}
          disabled={isDisabled}
        />
      </Form.Item>
    </Flex>
  );
};
